<template>
  <Box>
    <BoxHead
      icon="fas fa-briefcase"
      :link="link"
    >
      Cases
    </BoxHead>

    <LoaderWithText
      v-if="isFetching"
      size="3_5"
    />

    <div
      v-else
      class="cases-body"
    >
      <Stack
        v-if="topCases.length"
        gap="2"
        class="tw-grow"
      >
        <Stack
          gap="1"
          class="case-items"
        >
          <router-link
            v-for="caseItem in topCases"
            :key="caseItem.id"
            :to="caseLink(caseItem)"
            class="case-item"
          >
            <p class="case-subject">
              {{ caseItem.subject || `Case #${caseItem.case_number}` }}
            </p>
            <p class="case-status">
              {{ caseItem.status || '' }}
            </p>
          </router-link>
        </Stack>

        <div
          v-if="viewMore"
          style="margin-top: auto;"
        >
          <Button
            icon-before="fas fa-plus"
            :to="toPath"
            size="3_5"
            spacing="tight"
          >
            {{ viewMore }}
          </Button>
        </div>
      </Stack>

      <EmptyBox
        v-else
        size="3_5"
      >
        <Stack gap="2">
          <p>No cases found</p>
          <Button
            :to="toPath"
            icon-before="fas fa-plus"
            spacing="tight"
          >
            Create case
          </Button>
        </Stack>
      </EmptyBox>
    </div>
  </Box>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Box, BoxHead, Button, EmptyBox, LoaderWithText, Stack } from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';

const advisorStore = useAdvisorStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
});

const isFetching = ref(false);
const hasFetched = ref(false);
const cases = ref([]);

const toPath = { name: 'entity-cases' };
const link = computed(() => {
  return {
    to: toPath,
    label: 'View all',
  };
});

const caseLink = caseItem => ({
  name: 'entity-cases',
  query: { caseId: caseItem.id },
});

const topCases = computed(() => cases.value.slice(0, 10));
const viewMore = computed(() => cases.value.length > 10 ? `View ${cases.value.length - 10} more` : '');

const fetchData = async () => {
  isFetching.value = true;

  try {
    const response = await fetch(`/api/v1/entities/${props.entityExternalId}/cases/`, {
      method: 'GET',
      headers: advisorStore.headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    const data = await response.json();
    cases.value = data.cases || [];
  } catch (error) {
    console.error(`Error fetching`, error);
  } finally {
    isFetching.value = false;
    hasFetched.value = true;
  }
};

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.cases-body {
  display: flex;
  flex-grow: 1;
}

.case-item {
  display: flex;
  align-items: center;
  gap: 0.5em;
  color: var(--stk-text-strong);
  transition-duration: var(--stk-transition-duration);
  transition-property: color;

  &:hover {
    color: var(--stk-primary);
  }
}

.case-subject {
  font-size: 0.875em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-status {
  color: var(--stk-text-weak);
  font-size: 0.75em;
  margin-left: auto;
  white-space: nowrap;
  text-transform: capitalize;
}
</style>
